import React from "react";
import T from "prop-types";

import DisableBulkIcon from "@material-ui/icons/CheckBox";
import EnableBulkIcon from "@material-ui/icons/CheckBoxOutlineBlank";

import IconButton from "@material-ui/core/IconButton";
import { ColumnSelectionForm, EXCLUDE } from "./SelectionDialog";
import { ExcludeColumnsModal } from "./ExcludeColumnsModal";
import DatagridEx from "../../components/_extensions/react-admin/ra-ui-materialui/src/list/datagrid/DatagridEx";
import { userDataSessionColumnsStorage } from "./UserDataStorage";

// CustomizableDatagrid allows to show/hide columns dynamically
// the preferences are stored on server

const getColumnName = (fieldProps) => {
	const source = fieldProps?.source;
	if (source) return source;
	const label = fieldProps?.label;
	if (label && typeof label === "string") return label;
	if (label && typeof label === "object") return label?.props?.label;
	return undefined;
};

const getColumnNames = (children) => {
	return React.Children.map(children, (field) => getColumnName(field?.props)).filter(
		(item) => item
	);
};

const getColumnLabels = (children) => {
	return React.Children.map(
		children,
		(field) =>
			field && {
				source: getColumnName(field?.props),
				label: field?.props?.label,
			}
	).filter((fieldProps) => getColumnName(fieldProps));
};

const getFilteredExcludedColumns = (excludedColumns, children) => {
	const datagridColumns = getColumnNames(children);
	const newExcludedColumns = datagridColumns.reduce((accumulator, columnName) => {
		if (excludedColumns[columnName] === EXCLUDE) {
			accumulator[columnName] = EXCLUDE;
		}
		return accumulator;
	}, {});
	return newExcludedColumns;
};

const getExcludedColumns = (storage, resource, children) => {
	const excludedColumns = storage.get(resource) || {};
	return(getFilteredExcludedColumns(excludedColumns, children));
};

export const CustomizableDatagrid = ({ children, storage, TopComponent, ...props }) => {
	const { resource, filterValues, exposedColumns = [], canMultipleChanges, ...rest } = props;
	const [excludedColumns, setExcludedColumns] = React.useState(
		getExcludedColumns(storage, resource, children)
	);

	const toggleColumn = (columnName) => {
		const newState = { ...excludedColumns };
		if (newState[columnName] === EXCLUDE) {
			delete newState[columnName];
		} else {
			newState[columnName] = EXCLUDE;
		}
		setExcludedColumns(newState);
		if (newState) {
			storage.set(resource, newState);
		}
	};

	const handleSetBulkOpen = () => props.setIsBulkOpen(!props.isBulkOpen);

	const renderChild = (child) => {
		if (child) {
			const name = getColumnName(child.props);
			// Show children without source and label or children explicitly visible
			if (name && excludedColumns[name] === EXCLUDE) {
				return null;
			}
			return React.cloneElement(child, {});
		}
		return null;
	};
	return (
		<>
			{TopComponent ? TopComponent : null}
			<div id={"aboveTable"} style={{ display: "flex", alignItems: "center" }}>
				{canMultipleChanges ? (
					<IconButton color="primary" onClick={handleSetBulkOpen} style={{ padding: "12px 16px" }}>
						{props.isBulkOpen ? <EnableBulkIcon /> : <DisableBulkIcon />}
					</IconButton>
				) : null}

				<ExcludeColumnsModal
					resource={resource}
					selection={excludedColumns}
					columns={getColumnLabels(children)}
					toggleColumn={toggleColumn}
				/>

				{exposedColumns && (
					<ColumnSelectionForm
						formGroupProps={{ row: true, style: { marginLeft: "10px" } }}
						columns={getColumnLabels(children)}
						selection={excludedColumns}
						onColumnClicked={toggleColumn}
						resource={resource}
						filterColumns={exposedColumns}
					/>
				)}
			</div>
			<DatagridEx {...rest}>{React.Children.map(children, renderChild)}</DatagridEx>
		</>
	);
};

CustomizableDatagrid.propTypes = {
	storage: T.shape({
		get: T.func.isRequired,
		set: T.func.isRequired,
	}),
	resource: T.string,
};

CustomizableDatagrid.defaultProps = {
	storage: userDataSessionColumnsStorage,
};
